import { OWNER_LANG_BY_COUNTRY, type Lang } from "./i18n";

// The message a traveler sends must be in the *club's* language, not the
// visitor's. A lido in Pachino reading "Hi, I would like to check bed
// availability" is a worse lead than the same question in Italian, and it was
// going out in English on every language version of the site.
//
// Countries we have no dictionary for (Greece, Croatia, Portugal, Turkey, the
// UAE) fall back to English on purpose: English is what beach club staff in
// those places already handle from tourists all day. Guessing at Greek with a
// template would read worse than the language they expect.
export function clubLang(country: string): Lang {
  return OWNER_LANG_BY_COUNTRY[country] ?? "en";
}

const DATE_LOCALE: Record<Lang, string> = {
  en: "en-GB",
  nl: "nl-NL",
  it: "it-IT",
  de: "de-DE",
  fr: "fr-FR",
  es: "es-ES",
};

// "sabato 8 agosto" beats "2026-08-08" in a WhatsApp thread: the club reads the
// day of the week, which is the thing that decides whether they are full.
export function formatClubDate(iso: string, lang: Lang): string {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(DATE_LOCALE[lang], {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(d);
}

type Templates = {
  /** Nothing filled in yet: the plain "are you open, do you have room" opener. */
  bare: (club: string) => string;
  /** Date and bed count known, name optional. */
  request: (a: { club: string; beds: number; date: string; name: string }) => string;
};

const T: Record<Lang, Templates> = {
  it: {
    bare: (club) =>
      `Ciao ${club}, vi ho trovati su sunbedly. Avete lettini liberi nei prossimi giorni?`,
    request: ({ club, beds, date, name }) =>
      `Ciao ${club}, vi ho trovati su sunbedly. Avete ${beds} ${
        beds === 1 ? "lettino libero" : "lettini liberi"
      } ${date}?${name ? ` Grazie, ${name}` : ""}`,
  },
  es: {
    bare: (club) =>
      `Hola ${club}, os he encontrado en sunbedly. ¿Tenéis hamacas libres estos días?`,
    request: ({ club, beds, date, name }) =>
      `Hola ${club}, os he encontrado en sunbedly. ¿Tenéis ${beds} ${
        beds === 1 ? "hamaca libre" : "hamacas libres"
      } el ${date}?${name ? ` Gracias, ${name}` : ""}`,
  },
  fr: {
    bare: (club) =>
      `Bonjour ${club}, je vous ai trouvés sur sunbedly. Avez-vous des transats libres ces jours-ci ?`,
    request: ({ club, beds, date, name }) =>
      `Bonjour ${club}, je vous ai trouvés sur sunbedly. Avez-vous ${beds} transat${
        beds === 1 ? "" : "s"
      } libre${beds === 1 ? "" : "s"} le ${date} ?${name ? ` Merci, ${name}` : ""}`,
  },
  de: {
    bare: (club) =>
      `Hallo ${club}, ich habe euch über sunbedly gefunden. Habt ihr in den nächsten Tagen Liegen frei?`,
    request: ({ club, beds, date, name }) =>
      `Hallo ${club}, ich habe euch über sunbedly gefunden. Habt ihr am ${date} ${beds} ${
        beds === 1 ? "Liege" : "Liegen"
      } frei?${name ? ` Danke, ${name}` : ""}`,
  },
  nl: {
    bare: (club) =>
      `Hallo ${club}, ik vond jullie via sunbedly. Hebben jullie de komende dagen bedjes vrij?`,
    request: ({ club, beds, date, name }) =>
      `Hallo ${club}, ik vond jullie via sunbedly. Hebben jullie op ${date} ${beds} ${
        beds === 1 ? "bedje" : "bedjes"
      } vrij?${name ? ` Groet, ${name}` : ""}`,
  },
  en: {
    bare: (club) =>
      `Hi ${club}, I found you on sunbedly. Do you have any sunbeds free in the next few days?`,
    request: ({ club, beds, date, name }) =>
      `Hi ${club}, I found you on sunbedly. Do you have ${beds} sunbed${
        beds === 1 ? "" : "s"
      } free on ${date}?${name ? ` Thanks, ${name}` : ""}`,
  },
};

export function clubMessage(
  club: { name: string; country: string },
  req?: { visitDate: string; beds: number; name: string },
): string {
  const lang = clubLang(club.country);
  if (!req) return T[lang].bare(club.name);
  return T[lang].request({
    club: club.name,
    beds: req.beds,
    date: formatClubDate(req.visitDate, lang),
    name: req.name.trim(),
  });
}

export function whatsappHref(
  digits: string,
  club: { name: string; country: string },
  req?: { visitDate: string; beds: number; name: string },
): string {
  return `https://wa.me/${digits}?text=${encodeURIComponent(clubMessage(club, req))}`;
}

const SUBJECT: Record<Lang, string> = {
  it: "Disponibilità lettini",
  es: "Disponibilidad de hamacas",
  fr: "Disponibilité de transats",
  de: "Verfügbarkeit von Liegen",
  nl: "Beschikbaarheid ligbedden",
  en: "Sunbed availability",
};

export function mailtoHref(
  email: string,
  club: { name: string; country: string },
  req?: { visitDate: string; beds: number; name: string },
): string {
  const lang = clubLang(club.country);
  return `mailto:${email}?subject=${encodeURIComponent(SUBJECT[lang])}&body=${encodeURIComponent(
    clubMessage(club, req),
  )}`;
}

// Is this number a mobile?
//
// 87% of our WhatsApp buttons are built from a landline we found, not from a
// number anyone confirmed runs WhatsApp. A wa.me link to a landline dead-ends
// on "this number is not on WhatsApp", and a visitor who hits that screen does
// not come back and try the phone. They leave.
//
// Mobile ranges are strictly allocated per country, so this is a rule and not a
// guess: an Italian mobile always starts +39 3, a Spanish one +34 6 or 7, and
// so on. A mobile is very likely on WhatsApp; a landline almost never is. That
// is enough to decide which channel to put forward, without ever hiding the
// other one.
const MOBILE_PREFIX: [string, RegExp][] = [
  ["39", /^3/],        // Italy
  ["34", /^[67]/],     // Spain
  ["33", /^[67]/],     // France
  ["30", /^69/],       // Greece
  ["385", /^9/],       // Croatia
  ["351", /^9/],       // Portugal
  ["31", /^6/],        // Netherlands
  ["49", /^1[5-7]/],   // Germany
  ["971", /^5/],       // United Arab Emirates
];

export function looksMobile(phone: string | null): boolean {
  if (!phone) return false;
  const digits = phone.replace(/[^0-9]/g, "");
  for (const [cc, rest] of MOBILE_PREFIX) {
    if (digits.startsWith(cc) && rest.test(digits.slice(cc.length))) return true;
  }
  // An unknown country code is not a reason to assume the worst; the club may
  // well be on WhatsApp and the confirmation panel carries the phone anyway.
  return !MOBILE_PREFIX.some(([cc]) => digits.startsWith(cc));
}

/**
 * How much confidence we have that the WhatsApp handoff will actually land.
 * "confirmed" came from research, "likely" is a mobile, "unlikely" is a landline.
 */
export function whatsappConfidence(club: {
  whatsapp: string | null;
  phone: string | null;
}): "confirmed" | "likely" | "unlikely" {
  if (club.whatsapp) return "confirmed";
  return looksMobile(club.phone) ? "likely" : "unlikely";
}

// Prefer a phone number confirmed to run on WhatsApp; fall back to the plain
// phone (most Mediterranean beach clubs run their landline through WhatsApp
// too, even when we could not confirm that from a public source). wa.me simply
// fails to open a chat if the number turns out not to be on WhatsApp, so this
// is a safe best-effort that gets more traveler requests to the club fast.
export function bestWhatsappDigits(club: {
  whatsapp: string | null;
  phone: string | null;
}): string | null {
  if (club.whatsapp) return club.whatsapp;
  if (club.phone) {
    const digits = club.phone.split("/")[0].replace(/[^0-9]/g, "");
    return digits.length >= 8 ? digits : null;
  }
  return null;
}


// The club's own website, tagged so they can see where the visitor came from.
//
// This link carried `rel="noreferrer"`, which strips the Referer header, so a
// club looking at their own analytics saw our traffic filed under "Direct".
// We were erasing the single most useful thing we have for the outreach: proof
// rather than a claim. "We sent you 40 visitors last month" beats any pitch.
//
// `nofollow` stays. It is about search engines, not attribution, and 827
// outbound links to businesses we list are not editorial endorsements.
//
// UTM on top of the referrer, because the two fail in different ways. The
// referrer survives a club with any analytics at all; the tags survive a
// visitor whose browser sends no referrer, and they show up under a name the
// owner recognises instead of a bare domain.
export function clubWebsiteUrl(website: string): string {
  try {
    const url = new URL(website);
    // Never clobber a parameter the club's own site depends on.
    if (!url.searchParams.has("utm_source")) {
      url.searchParams.set("utm_source", "sunbedly.com");
      url.searchParams.set("utm_medium", "referral");
      url.searchParams.set("utm_campaign", "club-listing");
    }
    return url.toString();
  } catch {
    // A malformed URL in the database should still send someone to the club.
    // Losing the link is worse than losing the tag.
    return website;
  }
}
